import { useQuery } from "react-query";
import { sureApiCall } from "src/api/axiosClient/sureApi";
import { zodParseWithLog } from "src/api/apiErrorLogs";
import { z } from "zod";
import { isQueryReady } from "src/api/axiosClient/utils";
import i18next from "i18next";
import { AxiosError } from "axios";
import { DataListOptionSchema } from "src/api/sure-api/dataList/useGetAllDataList.ts";
import { SsqScoreSchema } from "src/api/sure-api/ssq/useGetOneSsq.ts";

export const getOneSsqVersionApiKey = "getOneSsqVersion" as const;

type Options = {
    enabled?: boolean;
};

export const useGetOneSsqVersion = (ssqVersionId: number | undefined, options?: Options) => {
    const { enabled = true } = options || {};

    const isEnabled = ssqVersionId !== undefined && enabled;
    const query = useQuery({
        cacheTime: 300000,
        staleTime: 180000,
        enabled: isEnabled,
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        retryOnMount: false,
        retry: (failureCount: number, error: unknown) => {
            if (ssqVersionId === undefined) {
                return false;
            }

            const status = (error as AxiosError).response?.status;
            if (status === 404) {
                return false;
            }

            return failureCount < 1;
        },
        queryKey: [getOneSsqVersionApiKey, ssqVersionId, i18next.resolvedLanguage],
        queryFn: async () => {
            if (!ssqVersionId) {
                return;
            }

            return await sureApiCall.get(`/api/ssq_versions/${ssqVersionId}/structure`);
        },
        select: (response) => {
            if (!response) {
                return;
            }

            const ssqVersion = response.data;
            const data = zodParseWithLog(ssqVersion, SsqVersionSchema, getOneSsqVersionApiKey);
            return data;
        },
    });

    const isReady = isQueryReady(query, isEnabled);

    return {
        ssqVersion: query.data,
        isReady,
        ...query,
    };
};

export const FieldSchema = z.object({
    type: z.union([
        z.literal("group"),
        z.literal("number"),
        z.literal("text"),
        z.literal("long_text"),
        z.literal("select"),
        z.literal("radio"),
        z.literal("checkbox"),
        z.literal("date"),
        z.literal("daterange"),
    ]),
});
export type Field = z.infer<typeof FieldSchema>;

export const UnitSchema = z.object({
    id: z.number(),
    title: z.string(),
    abbreviation: z.string(),
    code: z.string(),
    quantityKind: z.string().nullable().optional(),
    factorToBase: z.number().nullable().optional(),
    converterEligible: z.boolean().optional(),
});

export type Unit = z.infer<typeof UnitSchema>;

const ConverterSiUnitSchema = z.object({
    id: z.number(),
    code: z.string(),
    abbreviation: z.string(),
    factorToBase: z.number(),
});

const ConverterRecipeSchema = z.object({
    id: z.number(),
    title: z.string(),
    label: z.string().optional(),
    factor: z.number(),
    source: z.string().nullable().optional(),
    fromUnit: z.object({
        code: z.string(),
        abbreviation: z.string(),
    }),
    toUnit: z.object({
        code: z.string(),
        abbreviation: z.string(),
    }),
});

export const ConverterSchema = z
    .object({
        mode: z.enum(["off", "si_family", "recipes", "si_and_recipes"]),
        siUnits: ConverterSiUnitSchema.array(),
        recipes: ConverterRecipeSchema.array(),
    })
    .nullable();

export type Converter = z.infer<typeof ConverterSchema>;

const FormulaSchema = z.object({
    id: z.number(),
    score: z
        .object({
            id: z.number(),
            title: z.string().optional(),
            type: z.string(),
            displayMode: z.string().optional(),
            unit: UnitSchema.optional(),
            precision: z.number().optional(),
        })
        .optional(),
    formula: z.string().optional(),
    unit: UnitSchema.optional(),
});

export type Formula = z.infer<typeof FormulaSchema>;

const EmbeddedDataListVersionSchema = z.object({
    "@id": z.string().optional(),
    "@type": z.string().optional(),
    id: z.number(),
    dataList: z.object({
        "@id": z.string().optional(),
        "@type": z.string().optional(),
        id: z.number().optional(),
        code: z.string(),
    }),
    title: z.string(),
    options: DataListOptionSchema.array(),
    source: z.string().optional(),
    versionMajor: z.number().optional(),
    versionMinor: z.number().optional(),
    status: z.number().optional(),
});

const BasePropertySchema = z.object({
    id: z.number(),
    title: z.string(),
    name: z.string().optional(),
    hint: z.string().optional(),
    helperText: z.string().optional(),
    position: z.number().optional(),
    defaultValue: z.string().optional(),
    genericValue: z.string().optional(),
    defaultValueIds: z.array(z.number()).optional(),
    genericValueIds: z.array(z.number()).optional(),
    multivalue: z.boolean(),
    readonly: z.boolean(),
    hidden: z.boolean().optional(),
    field: FieldSchema.optional(),
    unit: UnitSchema.optional(),
    dataList: EmbeddedDataListVersionSchema.optional(),
    minValue: z.number().optional(),
    maxValue: z.number().optional(),
    precision: z.number().optional(),
    formulas: FormulaSchema.array(),
    converter: ConverterSchema.optional(),
});

export type Property = z.infer<typeof BasePropertySchema> & {
    children: Property[];
    parent?: string | Partial<z.infer<typeof BasePropertySchema>>;
};

export const PropertySchema: z.ZodType<Property> = BasePropertySchema.extend({
    children: z.lazy(() => PropertySchema.array()),
    parent: z.union([z.string(), BasePropertySchema.partial()]).optional(),
});

const BaseSsqSsqHeadingSsqPropertySchema = z.object({
    id: z.number(),
    ssqProperty: PropertySchema,
    position: z.number().optional(),
    hidden: z.boolean().optional(),
    propertyName: z.string().optional(),
    propertyTitle: z.string().optional(),
    propertyHint: z.string().optional(),
    propertyHelperText: z.string().optional(),
    propertyReadonly: z.boolean().optional(),
    propertyDataList: EmbeddedDataListVersionSchema.optional(),
    propertyDefaultValue: z.string().optional(),
    propertyGenericValue: z.string().optional(),
    parent: z.string().optional(),
});

export type SsqSsqHeadingSsqProperty = z.infer<typeof BaseSsqSsqHeadingSsqPropertySchema> & {
    children?: SsqSsqHeadingSsqProperty[];
};

export const SsqSsqHeadingSsqPropertySchema: z.ZodType<SsqSsqHeadingSsqProperty> =
    BaseSsqSsqHeadingSsqPropertySchema.extend({
        children: z.lazy(() => SsqSsqHeadingSsqPropertySchema.array().optional()),
    });

export const HeadingSchema = z.object({
    id: z.number(),
    title: z.string(),
    helperText: z.string().optional(),
    color: z.string().nullable().optional(),
});

export type Heading = z.infer<typeof HeadingSchema>;

const BaseSsqSsqHeadingSchema = z.object({
    id: z.number(),
    ssqVersionStructureId: z.number().optional(),
    ssqHeading: HeadingSchema,
    position: z.number(),
    hidden: z.boolean().optional(),
    helperText: z.string().optional(),
    ssqSsqHeadingSsqProperties: SsqSsqHeadingSsqPropertySchema.array(),
});

export type SsqSsqHeading = z.infer<typeof BaseSsqSsqHeadingSchema> & {
    children: SsqSsqHeading[];
};

export const SsqSsqHeadingSchema: z.ZodType<SsqSsqHeading> = BaseSsqSsqHeadingSchema.extend({
    children: z.lazy(() => SsqSsqHeadingSchema.array()),
});

const SourceVersionSchema = z.object({
    source: z.object({
        referenceType: z.string(),
    }),
});

const ReferenceMetadataSchema = z.object({
    id: z.number(),
    name: z.string(),
    sourceVersions: SourceVersionSchema.array(),
});

const SsqVersionSsqSchema = z.object({
    id: z.number(),
    title: z.string(),
    displayTitle: z.string().nullable().optional(),
    monolithic: z.boolean(),
    allowComments: z.boolean(),
    scores: z.array(SsqScoreSchema),
    availableScores: z.array(SsqScoreSchema),
    minimumCompletionRate: z.number().min(0).max(100),
});

export type SsqVersionSsq = z.infer<typeof SsqVersionSsqSchema>;

const IngredientGenericValueSchema = z.object({
    ingredientReferenceId: z.number(),
    ssqPropertyId: z.number(),
    value: z.string(),
});

export const SsqVersionSchema = z.object({
    id: z.number(),
    ssq: SsqVersionSsqSchema,
    versionMajor: z.number(),
    versionMinor: z.number(),
    status: z.number(),
    referenceMetadata: ReferenceMetadataSchema,
    parentSsqSsqHeadings: SsqSsqHeadingSchema.array(),
    ingredientGenericValues: IngredientGenericValueSchema.array().optional(),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
});

export type SsqVersion = z.infer<typeof SsqVersionSchema>;
